import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import AboutImg from "../assets/about-img.jpeg";
import drBhavesh from "../assets/drbhaveshgupta.jpeg";
import drIdris from "../assets/team/Dr-idris.jpeg";
import drAjinkya from "../assets/team/dr-ajinkya.png";
import drPranay from "../assets/team/dr-pranay.jpeg";

gsap.registerPlugin(ScrollTrigger);

/* ─────────────────────────────────────────────
   SHARED CLASS STRINGS
───────────────────────────────────────────── */
const cinzel = "font-['Cinzel',serif]";
const goldText = "bg-[linear-gradient(135deg,#c9943a,#ffe9a0,#c9943a)] bg-clip-text text-transparent";
const tagCls = `${cinzel} rounded border border-[#c9a84c]/25 px-2.5 py-1 text-[9.5px] uppercase tracking-[0.12em] text-[#c9a84c]/90`;
const btnPrimary = `${cinzel} inline-block rounded-md bg-[linear-gradient(135deg,#c9943a,#ffe9a0,#c9943a)] bg-[length:200%] bg-left px-8 py-3.5 text-[11px] font-bold uppercase tracking-[0.16em] text-[#1a0f02] transition-all duration-300 hover:-translate-y-0.5 hover:bg-right hover:shadow-[0_8px_24px_rgba(201,168,76,0.35)]`;
const btnPill = btnPrimary.replace("rounded-md", "rounded-full");
const inner = "relative z-[1] mx-auto max-w-[1200px] px-[18px] sm:px-6 lg:px-12";

/* ─────────────────────────────────────────────
   DATA
───────────────────────────────────────────── */
const stats = [
  {
    icon: (
      <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" width="34" height="34">
        <circle cx="32" cy="20" r="10" stroke="#c9a84c" strokeWidth="2.5" fill="none" />
        <path d="M14 50c0-9.94 8.06-18 18-18s18 8.06 18 18" stroke="#c9a84c" strokeWidth="2.5" strokeLinecap="round" fill="none" />
        <circle cx="44" cy="38" r="5" stroke="#c9a84c" strokeWidth="2" fill="none" />
        <path d="M44 35v3l2 2" stroke="#c9a84c" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="20" cy="38" r="5" stroke="#c9a84c" strokeWidth="2" fill="none" />
        <path d="M18 40l2-2 2 2" stroke="#c9a84c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    value: "10,000+",
    label: "Happy Patients",
  },
  {
    icon: (
      <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" width="34" height="34">
        <rect x="12" y="18" width="40" height="30" rx="4" stroke="#c9a84c" strokeWidth="2.5" fill="none" />
        <path d="M20 28h24M20 35h16" stroke="#c9a84c" strokeWidth="2" strokeLinecap="round" />
        <path d="M36 42l4-4 4 4" stroke="#c9a84c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M28 18v-4M36 18v-4" stroke="#c9a84c" strokeWidth="2" strokeLinecap="round" />
        <circle cx="44" cy="44" r="2" fill="#c9a84c" />
      </svg>
    ),
    value: "1300+",
    label: "Testimonials",
  },
  {
    icon: (
      <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" width="34" height="34">
        <circle cx="32" cy="32" r="18" stroke="#c9a84c" strokeWidth="2.5" fill="none" />
        <path d="M32 20v12l8 8" stroke="#c9a84c" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M20 14l-4-4M44 14l4-4" stroke="#c9a84c" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
    value: "14+",
    label: "Years Experience",
  },
  {
    icon: (
      <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" width="34" height="34">
        <path d="M32 12l4 8 9 1.3-6.5 6.3 1.5 9L32 32l-8 4.6 1.5-9L19 21.3l9-1.3L32 12z" stroke="#c9a84c" strokeWidth="2.5" strokeLinejoin="round" fill="none" />
        <path d="M20 46h24M24 50h16" stroke="#c9a84c" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
    value: "4",
    label: "Clinic Locations",
  },
];

const surgeons = [
  {
    name: "Dr. Bhavesh Gupta",
    role: "Facial Cosmetic & Hair Transplant Surgeon",
    place: "Navi Mumbai",
    badge: "Founder",
    featured: true,
    image: drBhavesh,
    desc: "Leads Mumbai Branch with 14+ years in hair restoration & leading authority in Sapphire FUE and DHI with 10,000+ successful procedures.",
    tags: ["AOMSI", "14+ Yrs", "10000+ Cases"],
  },
  {
    name: "Dr. Idris MD",
    role: "Facial Cosmetic & Hair Transplant Surgeon",
    place: "Bangalore",
    image: drIdris,
    desc: "Leads the Bangalore center with extensive experience of 15 yrs & 8000+ cases of Sapphire FUE & DHI hair transplant surgery.",
    tags: ["AOMSI Member", "15+ Yrs"],
  },
  {
    name: "Dr. Ajinkya Patil",
    role: "Facial Cosmetic & Hair Transplant Surgeon",
    place: "Pune",
    image: drAjinkya,
    desc: "Leads pune center with 8 years of experience in Sapphire FUE, FUT and hairloss management with focus on natural hairline design.",
    tags: ["FUE", "FUT", "PRP"],
  },
  {
    name: "Dr. Pranay Thakur",
    role: "Facial Cosmetic & Hair Transplant Surgeon",
    place: "Shirdi & Nashik",
    image: drPranay,
    desc: "Leads the Shirdi & Nashik centre with experience of around 5,000+ procedures and focus on precise, natural results.",
    tags: ["AOMSI Member", "5,000+ Cases"],
  },
];

const hairTruths = [
  {
    heading: "Your donor hair is a one-time budget.",
    desc: "Hair taken from the back of the scalp never grows back, so every graft has to be planned for your lifetime, not just one surgery.",
  },
  {
    heading: "A transplant doesn’t stop hair loss.",
    desc: "It restores what you’ve lost, but your existing hair can keep thinning. A good plan includes treatment to protect the hair you still have.",
  },
  {
    heading: "Graft count isn’t the goal. Graft survival is.",
    desc: "A big number means little if the grafts don’t take. How carefully grafts are handled and how long they stay outside the body decide how many actually grow.",
  },
  {
    heading: "A hairline is designed, not traced.",
    desc: "A hairline that suits you at 25 should still look natural at 55. That’s why doctors at Clinique designs every hairline by themselves.",
  },
  {
    heading: "Cheap surgery often costs the most.",
    desc: "An over-harvested donor area, visible scarring or an unnatural hairline can be hard, and sometimes impossible, to repair.",
  },
  {
    heading: "Ask who is actually doing your surgery.",
    desc: "Before you book, find out who designs your hairline and who makes the recipient slits.",
  },
];

/* ─────────────────────────────────────────────
   SMALL PRESENTATIONAL HELPERS
───────────────────────────────────────────── */
function Eyebrow({ children, center = false, dark = false }) {
  const line = dark ? "bg-[#c9a84c]/50" : "bg-[#c9a84c]";
  return (
    <p
      className={`${cinzel} mb-3 flex items-center gap-2.5 text-[10.5px] uppercase tracking-[0.28em] ${
        dark ? "text-[#c9a84c]/70" : "text-[#a07828]"
      } ${center ? "mb-2 justify-center gap-3" : ""}`}
    >
      <span className={`block h-px w-6 shrink-0 ${line}`} />
      {children}
      {center && <span className={`block h-px w-6 shrink-0 ${line}`} />}
    </p>
  );
}

function SectionHeading({ children }) {
  return (
    <h3 className={`${cinzel} mb-14 text-center text-[clamp(1.3rem,2vw,1.8rem)] font-black uppercase tracking-[0.06em] text-[#1a0f02]`}>
      {children}
    </h3>
  );
}

/* ─────────────────────────────────────────────
   THE HAIR TRUTH — horizontal carousel
   - Item width / gap / side padding are computed in px from the card width
     (see getTruthLayout), so the track can be moved with a simple translateX.
   - Desktop shows ~2.5 facts (the next one peeks in and is clipped by the card).
   - Mobile shows exactly 1 fact at a time.
───────────────────────────────────────────── */
function getTruthLayout(windowWidth) {
  if (windowWidth >= 1024) return { perView: 2.5, pad: 64, gap: 56 }; // matches lg:px-16
  if (windowWidth >= 640) return { perView: 1.7, pad: 40, gap: 40 }; //  matches sm:px-10
  return { perView: 1, pad: 24, gap: 24 }; //                           matches px-6
}

const TRUTH_INTERVAL = 4500; // ms between automatic advances
const GLOW_OFF = "drop-shadow(0 0 0px rgba(255,233,160,0))";
const GLOW_ON = "drop-shadow(0 0 6px rgba(255,233,160,0.55))";
// Slightly irregular, overshooting "pen" stroke + a thinner partial echo stroke (viewBox 0 0 100 100)
const RING_MAIN =
  "M 46 5 C 74 3 96 24 95 51 C 94 78 72 97 47 95 C 22 93 4 74 5 48 C 6 24 26 6 52 5 C 58 4.6 63 5.6 67 7.5";
const RING_ECHO = "M 58 10 C 80 14 93 34 91 56 C 89 76 70 92 48 91";

function ArrowButton({ direction, onClick, disabled }) {
  const isPrev = direction === "prev";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={isPrev ? "Previous fact" : "Next fact"}
      className="flex h-11 w-11 items-center justify-center rounded-full border border-[#c9a84c]/40 bg-[#c9a84c]/10 text-[#ffe9a0] transition-all duration-300 enabled:hover:-translate-y-0.5 enabled:hover:border-transparent enabled:hover:bg-[linear-gradient(135deg,#c9943a,#ffe9a0,#c9943a)] enabled:hover:text-[#1a0f02] enabled:hover:shadow-[0_8px_22px_rgba(201,168,76,0.35)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#c9a84c] disabled:cursor-not-allowed disabled:opacity-35 sm:h-12 sm:w-12"
    >
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d={isPrev ? "M15 5l-7 7 7 7" : "M9 5l7 7-7 7"} />
      </svg>
    </button>
  );
}

function HairTruth() {
  const rootRef = useRef(null);
  const headRef = useRef(null);
  const subRef = useRef(null);
  const viewportRef = useRef(null);
  const touchRef = useRef({ x: 0, y: 0 });
  // per-fact DOM handles used by GSAP: { num, text, svg, main, echo }
  const els = useRef(hairTruths.map(() => ({})));

  // Mutable state that must NOT trigger re-renders
  const timerRef = useRef(null);
  const touchTimerRef = useRef(null);
  const tlRef = useRef(null);
  const ctxRef = useRef(null);
  const lastActiveRef = useRef(null);
  const hoverRef = useRef(false);
  const touchingRef = useRef(false);
  const inViewRef = useRef(false);
  const startedRef = useRef(false);
  const reducedRef = useRef(
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  const [index, setIndex] = useState(0);
  const [started, setStarted] = useState(false);
  const [dims, setDims] = useState({ itemWidth: 280, gap: 24, pad: 24, step: 304, maxTranslate: 0, maxIndex: hairTruths.length - 1 });
  const dimsRef = useRef(dims);

  /* ── Autoplay: one timeout at a time, restarted whenever the index changes ── */
  const schedule = useCallback(() => {
    const canRun = () =>
      startedRef.current &&
      !reducedRef.current &&
      !hoverRef.current &&
      !touchingRef.current &&
      inViewRef.current &&
      !document.hidden;

    clearTimeout(timerRef.current);
    if (!canRun()) return;
    timerRef.current = setTimeout(() => {
      if (!canRun()) return;
      const max = dimsRef.current.maxIndex;
      if (max <= 0) return;
      setIndex((i) => (i >= max ? 0 : i + 1)); // after the last position, glide back to the start
    }, TRUTH_INTERVAL);
  }, []);

  useEffect(() => {
    schedule();
    return () => clearTimeout(timerRef.current);
  }, [index, started, schedule]);

  // Tab visibility + reduced-motion preference
  useEffect(() => {
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    reducedRef.current = mql.matches;
    const onVis = () => { if (!document.hidden) schedule(); };
    const onMotion = () => { reducedRef.current = mql.matches; schedule(); };
    document.addEventListener("visibilitychange", onVis);
    mql.addEventListener("change", onMotion);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      mql.removeEventListener("change", onMotion);
      clearTimeout(touchTimerRef.current);
    };
  }, [schedule]);

  /* ── Measure the card: item width, step and the furthest scroll position ── */
  useLayoutEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;

    const measure = () => {
      const width = vp.clientWidth;
      const { perView, pad, gap } = getTruthLayout(window.innerWidth);
      const itemWidth = perView === 1 ? width - pad * 2 : (width - pad - gap * Math.floor(perView)) / perView;
      const step = itemWidth + gap;
      const total = pad * 2 + hairTruths.length * itemWidth + (hairTruths.length - 1) * gap;
      const maxTranslate = Math.max(0, total - width);
      const maxIndex = step > 0 ? Math.ceil(Number((maxTranslate / step).toFixed(3))) : 0;
      const next = { itemWidth, gap, pad, step, maxTranslate, maxIndex };
      dimsRef.current = next;
      setDims(next);
      setIndex((i) => Math.min(i, maxIndex));
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(vp);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  /* ── GSAP: scroll reveal, "started" trigger, in-view tracking. All inside one context
        so ctx.revert() on unmount kills every tween + ScrollTrigger created here. ── */
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const ease = "power3.out";
      const all = els.current;

      // resting state: rings undrawn, facts slightly dimmed until active
      gsap.set(all.flatMap((t) => [t.main, t.echo]).filter(Boolean), { strokeDashoffset: 1, opacity: 0 });
      gsap.set(all.map((t) => t.text).filter(Boolean), { opacity: 0.55 });
      gsap.set(all.map((t) => t.num).filter(Boolean), { opacity: 0.6 });

      if (!reducedRef.current) {
        const st = { trigger: rootRef.current, start: "top 85%", toggleActions: "play none none none" };
        gsap.fromTo([headRef.current, subRef.current], { opacity: 0, y: 28 }, { opacity: 1, y: 0, duration: 0.8, ease, stagger: 0.12, scrollTrigger: st });
        gsap.fromTo(viewportRef.current, { opacity: 0, y: 36 }, { opacity: 1, y: 0, duration: 0.9, ease, delay: 0.2, scrollTrigger: st });
      }

      // first visible → begin ring drawing + autoplay (only once)
      ScrollTrigger.create({
        trigger: rootRef.current,
        start: "top 75%",
        onEnter: () => {
          if (startedRef.current) return;
          startedRef.current = true;
          setStarted(true);
        },
      });

      // pause autoplay while the section is off-screen
      ScrollTrigger.create({
        trigger: rootRef.current,
        start: "top bottom",
        end: "bottom top",
        onToggle: (self) => {
          inViewRef.current = self.isActive;
          if (self.isActive) schedule();
        },
      });
    }, rootRef);
    ctxRef.current = ctx;

    return () => {
      clearTimeout(timerRef.current);
      tlRef.current?.kill();
      tlRef.current = null;
      ctx.revert();
      ctxRef.current = null;
      lastActiveRef.current = null;
    };
  }, [schedule]);

  /* ── GSAP: draw the ring + entrance animation for the active fact ── */
  useEffect(() => {
    if (!started || !ctxRef.current) return;
    const prev = lastActiveRef.current;
    lastActiveRef.current = index;

    ctxRef.current.add(() => {
      const reduced = reducedRef.current;
      tlRef.current?.kill();

      const deactivate = (i) => {
        const t = els.current[i];
        if (!t || !t.main) return;
        gsap.killTweensOf([t.num, t.text, t.svg, t.main, t.echo]);
        if (reduced) {
          gsap.set(t.text, { opacity: 0.55, y: 0 });
          gsap.set(t.num, { opacity: 0.6, scale: 1 });
          gsap.set([t.main, t.echo], { strokeDashoffset: 1, opacity: 0 });
          gsap.set(t.svg, { filter: GLOW_OFF });
          return;
        }
        gsap.to(t.text, { opacity: 0.55, y: 0, duration: 0.5, ease: "power1.out" });
        gsap.to(t.num, { opacity: 0.6, scale: 1, duration: 0.5, ease: "power1.out" });
        gsap.to(t.svg, { filter: GLOW_OFF, duration: 0.4 });
        gsap.to([t.main, t.echo], {
          strokeDashoffset: 1, duration: 0.6, ease: "power2.in",
          onComplete: () => gsap.set([t.main, t.echo], { opacity: 0 }),
        });
      };

      const activate = (i) => {
        const t = els.current[i];
        if (!t || !t.main) return;
        gsap.killTweensOf([t.num, t.text, t.svg, t.main, t.echo]);

        if (reduced) {
          gsap.set([t.num, t.text], { opacity: 1, y: 0, scale: 1 });
          gsap.set(t.main, { strokeDashoffset: 0, opacity: 1 });
          gsap.set(t.echo, { strokeDashoffset: 0, opacity: 0.45 });
          gsap.set(t.svg, { filter: GLOW_ON });
          return;
        }

        // reset → redraw from the start every time this fact becomes active
        gsap.set(t.main, { strokeDashoffset: 1, opacity: 1 });
        gsap.set(t.echo, { strokeDashoffset: 1, opacity: 0.45 });
        gsap.set(t.svg, { filter: GLOW_OFF });

        const tl = gsap.timeline({ defaults: { ease: "power2.out" } });
        tl.fromTo(t.num, { scale: 0.9, opacity: 0.5 }, { scale: 1, opacity: 1, duration: 0.8 }, 0)
          .fromTo(t.text, { y: 14, opacity: 0.5 }, { y: 0, opacity: 1, duration: 0.8 }, 0.08)
          .to(t.main, { strokeDashoffset: 0, duration: 1.5, ease: "power2.inOut" }, 0.05)
          .to(t.echo, { strokeDashoffset: 0, duration: 1.2, ease: "power2.inOut" }, 0.4)
          .to(t.svg, { filter: GLOW_ON, duration: 0.6, ease: "sine.out" }, 1.35);
        tlRef.current = tl;
      };

      if (prev !== null && prev !== index) deactivate(prev);
      activate(index);
    });
  }, [index, started]);

  const canPrev = index > 0;
  const canNext = index < dims.maxIndex;
  const prev = () => setIndex((i) => Math.max(0, i - 1));
  const next = () => setIndex((i) => Math.min(dimsRef.current.maxIndex, i + 1));

  // Last position is clamped so the final fact sits flush with the right padding — no empty space
  const translateX = Math.min(index * dims.step, dims.maxTranslate);

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
    if (e.key === "ArrowRight") { e.preventDefault(); next(); }
  };

  // Touch: pause autoplay while interacting, resume after a short idle period
  const endTouchPause = () => {
    clearTimeout(touchTimerRef.current);
    touchTimerRef.current = setTimeout(() => {
      touchingRef.current = false;
      schedule();
    }, 3500);
  };
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY };
    touchingRef.current = true;
    clearTimeout(touchTimerRef.current);
    clearTimeout(timerRef.current);
  };
  const onTouchEnd = (e) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) next();
      else prev();
    }
    endTouchPause();
  };

  return (
    <div ref={rootRef} className="relative">
      <section
        aria-labelledby="hair-truth-heading"
        onPointerEnter={(e) => { if (e.pointerType === "mouse") { hoverRef.current = true; clearTimeout(timerRef.current); } }}
        onPointerLeave={(e) => { if (e.pointerType === "mouse") { hoverRef.current = false; schedule(); } }}
        className="relative overflow-hidden rounded-[24px] border border-[#c9a84c]/25 pb-16 pt-12 text-white shadow-[0_2px_4px_rgba(0,0,0,0.3),0_16px_40px_rgba(0,0,0,0.35),0_40px_80px_rgba(201,168,76,0.08)] sm:rounded-[32px] sm:pt-16"
        style={{
          background:
            "radial-gradient(ellipse at 88% 0%, rgba(201,168,76,0.18), transparent 55%), radial-gradient(ellipse at 0% 100%, rgba(201,168,76,0.09), transparent 50%), linear-gradient(145deg, #1a0f02 0%, #0e0905 70%)",
        }}
      >
        {/* fine grain */}
        <div
          className="pointer-events-none absolute inset-0 z-0 opacity-50 mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.14'/%3E%3C/svg%3E\")",
          }}
        />
        {/* top gold hairline */}
        <span className="pointer-events-none absolute inset-x-0 top-0 z-[1] h-px bg-[linear-gradient(90deg,transparent,rgba(201,168,76,0.6),transparent)]" />

        {/* Header */}
        <div className="relative z-[1] flex flex-col gap-6 px-6 sm:px-10 md:flex-row md:items-start md:justify-between lg:px-16">
          <div className="max-w-[640px]">
            <h3
              ref={headRef}
              id="hair-truth-heading"
              className={`${cinzel} mb-4 text-[clamp(2rem,4.2vw,3.1rem)] font-semibold leading-[1.1] tracking-[0.02em] text-white`}
            >
              The Hair Truth.
            </h3>
            <p ref={subRef} className="text-[14.5px] leading-[1.75] text-white/75 sm:text-[15.5px]">
              Most failed hair transplants aren’t bad luck. They happen because nobody explained the facts before
              surgery. Here’s what every patient deserves to know.
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-3 md:pt-2">
            <ArrowButton direction="prev" onClick={prev} disabled={!canPrev} />
            <ArrowButton direction="next" onClick={next} disabled={!canNext} />
          </div>
        </div>

        {/* separator */}
        <div className="relative z-[1] mx-6 mt-8 h-px bg-[linear-gradient(90deg,rgba(201,168,76,0.4),rgba(201,168,76,0.12),transparent)] sm:mx-10 lg:mx-16" />

        {/* Carousel viewport — clipped at the card edges so the next fact peeks in */}
        <div
          ref={viewportRef}
          role="region"
          aria-roledescription="carousel"
          aria-label="The Hair Truth facts"
          tabIndex={0}
          onKeyDown={onKeyDown}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          onTouchCancel={endTouchPause}
          className="relative z-[1] mt-8 overflow-hidden focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#c9a84c]/70 sm:mt-10"
          style={{ touchAction: "pan-y" }}
        >
          <ul
            className="flex w-max items-start transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:duration-150"
            style={{
              columnGap: dims.gap,
              paddingLeft: dims.pad,
              paddingRight: dims.pad,
              transform: `translate3d(${-translateX}px,0,0)`,
              willChange: "transform",
            }}
          >
            {hairTruths.map((fact, i) => (
              <li
                key={fact.heading}
                aria-label={`Fact ${i + 1} of ${hairTruths.length}`}
                className="flex shrink-0 items-start gap-3 sm:gap-5"
                style={{ width: dims.itemWidth }}
              >
                {/* number + hand-drawn ring (square box so the circle always scales with the number) */}
                <div className="relative flex h-[5.25rem] w-[5.25rem] shrink-0 items-center justify-center sm:h-[6.25rem] sm:w-[6.25rem] lg:h-28 lg:w-28">
                  <svg
                    ref={(el) => { els.current[i].svg = el; }}
                    viewBox="0 0 100 100"
                    fill="none"
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
                    style={{ filter: GLOW_OFF }}
                  >
                    {/* faint resting track */}
                    <circle cx="50" cy="50" r="45.5" stroke="rgba(201,168,76,0.12)" strokeWidth="0.8" />
                    {/* thinner echo stroke */}
                    <path
                      ref={(el) => { els.current[i].echo = el; }}
                      d={RING_ECHO}
                      pathLength={1}
                      stroke="#ffe9a0"
                      strokeWidth="0.9"
                      strokeLinecap="round"
                      style={{ strokeDasharray: "1", strokeDashoffset: "1", opacity: 0 }}
                    />
                    {/* main pen stroke */}
                    <path
                      ref={(el) => { els.current[i].main = el; }}
                      d={RING_MAIN}
                      pathLength={1}
                      stroke="#c9a84c"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ strokeDasharray: "1", strokeDashoffset: "1", opacity: 0 }}
                    />
                  </svg>
                  <span
                    ref={(el) => { els.current[i].num = el; }}
                    className="relative block bg-[linear-gradient(135deg,#ffe9a0,#c9a84c)] bg-clip-text pb-[0.04em] font-['Raleway',sans-serif] text-[2.75rem] font-light leading-none text-transparent sm:text-[3.4rem] lg:text-[3.9rem]"
                  >
                    {i + 1}.
                  </span>
                </div>

                <div ref={(el) => { els.current[i].text = el; }} className="min-w-0 pt-1 sm:pt-3">
                  <h4 className="mb-2.5 font-['Raleway',sans-serif] text-[18px] font-semibold leading-[1.25] text-white sm:text-[20px] lg:text-[21px]">
                    {fact.heading}
                  </h4>
                  <p className="font-['Raleway',sans-serif] text-[13.5px] leading-[1.7] text-white/75 sm:text-[14px]">
                    {fact.desc}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Book a Consultation — straddles the bottom edge of the card (outside its overflow clip) */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] flex translate-y-1/2 justify-center px-4">
        <a
          href="/khargharNaviMumbai.php"
          className={`${btnPill} pointer-events-auto whitespace-nowrap shadow-[0_10px_28px_rgba(0,0,0,0.35),0_0_0_1px_rgba(201,168,76,0.35)]`}
        >
          Book a Consultation
        </a>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────── */
export default function About() {
  const sectionRef = useRef(null);
  const eyebrowRef = useRef(null);
  const headingRef = useRef(null);
  const bodyRef = useRef(null);
  const body2Ref = useRef(null);
  const imageRef = useRef(null);
  const statsRowRef = useRef(null);
  const statItemsRef = useRef([]);
  const teamRef = useRef(null);
  const teamItemsRef = useRef([]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const ease = "power3.out";

      const fadeUp = (el, trigger, delay = 0) => {
        if (!el) return;
        gsap.fromTo(
          el,
          { opacity: 0, y: 32 },
          {
            opacity: 1, y: 0, duration: 0.75, ease, delay,
            scrollTrigger: { trigger: trigger || el, start: "top 88%", toggleActions: "play none none reverse" },
          }
        );
      };

      fadeUp(eyebrowRef.current);
      fadeUp(headingRef.current, null, 0.08);
      fadeUp(bodyRef.current, null, 0.14);
      fadeUp(body2Ref.current, null, 0.2);

      gsap.fromTo(
        imageRef.current,
        { opacity: 0, x: 50, scale: 0.96 },
        {
          opacity: 1, x: 0, scale: 1, duration: 0.9, ease,
          scrollTrigger: { trigger: imageRef.current, start: "top 85%", toggleActions: "play none none reverse" },
        }
      );

      // clearProps lets the Tailwind hover:translate classes work after the entrance finishes
      statItemsRef.current.forEach((el, i) => {
        if (!el) return;
        gsap.fromTo(
          el,
          { opacity: 0, y: 48, rotateX: 18 },
          {
            opacity: 1, y: 0, rotateX: 0, duration: 0.7, ease, delay: i * 0.1, clearProps: "transform",
            scrollTrigger: { trigger: statsRowRef.current, start: "top 88%", toggleActions: "play none none reverse" },
          }
        );
      });

      teamItemsRef.current.forEach((el, i) => {
        if (!el) return;
        gsap.fromTo(
          el,
          { opacity: 0, y: 40, scale: 0.96 },
          {
            opacity: 1, y: 0, scale: 1, duration: 0.65, ease, delay: i * 0.1, clearProps: "transform",
            scrollTrigger: { trigger: teamRef.current, start: "top 85%", toggleActions: "play none none reverse" },
          }
        );
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&family=Raleway:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&display=swap"
        rel="stylesheet"
      />
      {/* Only thing Tailwind can't express inline: the keyframes for the floating badge */}
      <style>{`@keyframes abFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}`}</style>

      <section ref={sectionRef} className="relative w-full overflow-hidden bg-[#f2ece0] font-['Raleway',sans-serif]">
        {/* grain overlay */}
        <div
          className="pointer-events-none absolute inset-0 z-0 opacity-60"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E\")",
          }}
        />

        {/* ═══════════ HERO TOP ═══════════ */}
        <div className={inner}>
          <div className="grid grid-cols-1 items-start gap-10 pb-5 pt-14 md:grid-cols-2 md:gap-16 md:pt-20">
            {/* LEFT */}
            <div>
              <div ref={eyebrowRef}>
                <Eyebrow>Hair Restoration Clinique</Eyebrow>
              </div>
              <h2
                ref={headingRef}
                className={`${cinzel} mb-5 text-[clamp(1.65rem,2.9vw,2.5rem)] font-black uppercase leading-[1.1] tracking-[0.04em] text-[#1a0f02]`}
              >
                Hair Transplant
                <br />
                Clinique Internationale
              </h2>
              <p ref={bodyRef} className="mb-[18px] max-w-full text-sm leading-[1.88] text-[#3a2e1e] md:max-w-[480px]">
                Clinique is a super-specialised Hair Restoration Center where each and every treatment related to hair
                is done at an affordable cost. This project is initiated by our Founder,
                <strong className="font-semibold"> Dr. Bhavesh Gupta</strong>, who is known in the field of Hair
                Restoration. Clinique Internationale's first Center was in Mumbai, established in early 2012. As of now
                we are placed at 4 locations across India: Navi Mumbai (Kharghar), Bangalore, Pune &amp; Shirdi.
              </p>
              <p ref={body2Ref} className="mb-7 max-w-full text-sm leading-[1.88] text-[#3a2e1e] md:max-w-[480px]">
                Our Center caters to clients from all walks of life. Over the years, patient reviews, client logos, case
                studies and results have brought consistency to our work. Our team of surgeons work with the finest
                technicians to design, create and produce hair transplant results that we are proud of.{" "}
                <a
                  href="/description.php"
                  className={`${cinzel} border-b border-[#c9a84c]/40 pb-0.5 text-[10.5px] font-bold tracking-[0.14em] text-[#c9a84c] no-underline transition-colors duration-200 hover:border-[#a07828] hover:text-[#a07828]`}
                >
                  Read More
                </a>
              </p>
            </div>

            {/* RIGHT IMAGE */}
            <div ref={imageRef} className="group relative aspect-[4/3] shrink-0">
              <img
                src={AboutImg}
                alt="Hair transplant procedure at Clinique Internationale"
                className="block h-[120%] w-[110%] rounded-[14px] object-contain object-top transition-transform duration-[600ms] group-hover:scale-[1.03]"
              />
              <span className="pointer-events-none absolute inset-0 z-[2] rounded-[14px] border border-[#c9a84c]/30" />
              <span className="pointer-events-none absolute left-3 top-3 z-[3] h-[22px] w-[22px] border-l-2 border-t-2 border-[#c9a84c]" />
              <span className="pointer-events-none absolute bottom-3 right-3 z-[3] h-[22px] w-[22px] border-b-2 border-r-2 border-[#c9a84c]" />

              {/* floating trust badge */}
              <div className="absolute right-2 top-3 z-[5] hidden animate-[abFloat_3.5s_ease-in-out_infinite] items-center gap-2 rounded-[10px] border border-[#c9a84c]/30 bg-white px-3.5 py-2.5 shadow-[0_8px_28px_rgba(90,60,10,0.13)] sm:flex lg:-right-[18px] lg:top-4">
                <div>
                  <div className="text-[13px] tracking-[1px] text-[#fbbc04]">★★★★★</div>
                  <div className="text-[11px] text-[#3a2e1e]">
                    <strong className="block text-xs font-semibold text-[#1a0f02]">4.9 / 5.0</strong>
                    Google Reviews
                  </div>
                </div>
              </div>

              {/* image label */}
              <div className="absolute bottom-4 left-4 z-[4] rounded-lg border border-[#c9a84c]/25 bg-[#1a0f02]/70 px-3.5 py-2 backdrop-blur-[8px]">
                <div className={`${cinzel} text-[11px] font-bold tracking-[0.1em] text-[#c9a84c]`}>Clinique Internationale</div>
                <div className="mt-0.5 text-[11px] text-white/70">Est. 2012 · Navi Mumbai, India</div>
              </div>
            </div>
          </div>
        </div>

        {/* ═══════════ STATS ═══════════ */}
        <div className="h-12 hidden lg:block" />
        <div className="-mt-10 px-[18px] sm:px-6 lg:px-12 hidden lg:block">
          <div ref={statsRowRef} className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-5">
            {stats.map((s, i) => (
              <div
                key={i}
                ref={(el) => (statItemsRef.current[i] = el)}
                className="relative flex flex-col items-center justify-center gap-2.5 overflow-hidden rounded-[18px] border border-[#c9a84c]/20 bg-white px-3.5 pb-[22px] pt-7 text-center shadow-[0_2px_4px_rgba(90,60,10,0.05),0_8px_24px_rgba(90,60,10,0.08),0_24px_48px_rgba(90,60,10,0.06)] transition-all duration-[350ms] hover:-translate-y-[7px] hover:shadow-[0_4px_8px_rgba(90,60,10,0.07),0_18px_44px_rgba(90,60,10,0.13),0_44px_72px_rgba(90,60,10,0.08)] sm:gap-3.5 sm:px-5 sm:pb-[30px] sm:pt-9"
              >
                <span className="absolute left-4 right-4 top-0 h-px bg-[linear-gradient(90deg,transparent,rgba(201,168,76,0.55),transparent)]" />
                <span className="absolute inset-x-0 bottom-0 h-[3px] bg-[linear-gradient(90deg,transparent,rgba(201,168,76,0.18),transparent)]" />
                <div className="flex h-14 w-14 items-center justify-center rounded-full border border-[#c9a84c]/20 bg-[linear-gradient(145deg,rgba(201,168,76,0.1),rgba(201,168,76,0.03))] shadow-[0_2px_12px_rgba(201,168,76,0.1)] sm:h-[68px] sm:w-[68px]">
                  {s.icon}
                </div>
                <div>
                  <p className={`${cinzel} ${goldText} mb-[5px] text-[1.55rem] font-black leading-none sm:text-[1.9rem]`}>{s.value}</p>
                  <p className="text-[11.5px] font-medium leading-[1.4] tracking-[0.04em] text-[#6b5535] sm:text-[12.5px]">{s.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ═══════════ DARK BAND — Lead surgeon + team (desktop/tablet only) ═══════════ */}
<div className="hidden h-[72px] md:block" />
<div className="hidden h-px w-full bg-[linear-gradient(90deg,transparent,rgba(201,168,76,0.35),transparent)] md:block" />
<div className="hidden bg-[#0e0905] py-[72px] md:block">
          <div className={inner}>
            <div ref={teamRef} className="-mt-4">
              <Eyebrow dark>Our Surgeons Across India</Eyebrow>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-flow-col lg:auto-cols-fr">
                {surgeons.map((s, i) => (
                  <div
                    key={s.name}
                    ref={(el) => (teamItemsRef.current[i] = el)}
                    className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border p-3.5 transition-all duration-300 hover:-translate-y-1.5 ${
                      s.featured
                        ? "border-[#c9a84c] bg-[linear-gradient(160deg,rgba(201,168,76,0.2),rgba(201,168,76,0.04))] shadow-[0_0_0_1px_rgba(201,168,76,0.3),0_16px_48px_rgba(201,168,76,0.2)]"
                        : "border-[#c9a84c]/20 bg-white/[0.04] hover:border-[#c9a84c]/60"
                    }`}
                  >
                    {s.featured && (
                      <span className="absolute inset-x-0 top-0 h-[2px] bg-[linear-gradient(90deg,transparent,#ffe9a0,transparent)]" />
                    )}
                    <div
                      className={`relative aspect-[4/5] shrink-0 overflow-hidden rounded-xl ${
                        s.featured ? "ring-2 ring-[#c9a84c]/80" : ""
                      }`}
                    >
                      <img
                        src={s.image}
                        alt={`${s.name} — ${s.role}, ${s.place}`}
                        className="block h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      {s.badge && (
                        <span className={`${cinzel} absolute right-3 top-3 rounded-full bg-[linear-gradient(135deg,#c9943a,#ffe9a0,#c9943a)] px-3 py-[5px] text-[10px] font-bold uppercase tracking-[0.16em] text-[#1a0f02] shadow-[0_4px_14px_rgba(0,0,0,0.4)]`}>
                          {s.badge}
                        </span>
                      )}
                      <span className={`${cinzel} absolute bottom-3 left-3 rounded-full bg-[#c9a84c]/95 px-3 py-[5px] text-[10px] uppercase tracking-[0.16em] text-[#1a1a1a]`}>
                        {s.place}
                      </span>
                    </div>
                    <div className="flex flex-1 flex-col px-1.5 pb-1.5 pt-[18px]">
                      <div className={`${cinzel} mb-1 text-base ${s.featured ? "text-[#ffe9a0]" : "text-white"}`}>{s.name}</div>
                      <div className="mb-2.5 text-xs text-[#c9a84c]/85">{s.role}</div>
                      <p className={`mb-3.5 text-[13px] leading-[1.6] ${s.featured ? "text-white/80" : "text-white/65"}`}>{s.desc}</p>
                      <div className="mt-auto flex flex-wrap gap-1.5 border-t border-[#c9a84c]/15 pt-3.5">
                        {s.tags.map((t) => (
                          <span key={t} className={tagCls}>{t}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ═══════════ THE HAIR TRUTH ═══════════ */}
        <div className={`${inner} pb-14 pt-16 sm:pt-20`}>
          <div>
            <HairTruth />
          </div>
        </div>

        {/* ═══════════ PRICING ═══════════ */}
        <div className={inner}>
          <div>
            <Eyebrow center>Pricing</Eyebrow>
            <SectionHeading>Our Hair Transplant Package</SectionHeading>

            <div className="mx-auto mb-6 grid max-w-[900px] grid-cols-2 gap-1.5 min-[380px]:gap-2.5 sm:gap-7">
              {[
                {
                  head: "Classic",
                  rows: ["₹30 per Graft", "Sapphire FUE", "Pre-Operative Tests", "Head wash + PRP", "Hair Growth Kit (3 months)"],
                },
                {
                  head: "Premium",
                  rows: ["₹50 Per Graft", "Sapphire FUE with direct implantation", "Pre-Operative Tests", "Head wash + PRP", "Gold Hair Growth Kit (3 months)"],
                },
              ].map((pkg) => (
                <div
                  key={pkg.head}
                  className="relative overflow-hidden rounded-2xl border border-[#c9a84c]/30 bg-[linear-gradient(145deg,#161008,#0e0905)] shadow-[0_2px_4px_rgba(0,0,0,0.3),0_8px_24px_rgba(0,0,0,0.35),0_24px_56px_rgba(0,0,0,0.3),0_44px_90px_rgba(201,168,76,0.08)] transition-all duration-500 hover:[transform:perspective(1200px)_rotateX(1.5deg)_translateY(-10px)] hover:shadow-[0_4px_8px_rgba(0,0,0,0.35),0_18px_40px_rgba(0,0,0,0.4),0_40px_80px_rgba(0,0,0,0.35),0_70px_120px_rgba(201,168,76,0.14)]"
                >
                  <span className="absolute inset-x-0 top-0 h-px bg-[linear-gradient(90deg,transparent,rgba(201,168,76,0.5),transparent)]" />
                  <div className={`${cinzel} border-b border-[#c9a84c]/20 bg-[linear-gradient(145deg,rgba(201,168,76,0.14),rgba(201,168,76,0.03))] px-2 py-3.5 text-center text-sm font-bold tracking-[0.04em] text-[#c9a84c] sm:px-5 sm:py-6 sm:text-[22px] sm:tracking-normal`}>
                    {pkg.head}
                  </div>
                  {pkg.rows.map((r) => (
                    <div
                      key={r}
                      className="border-b border-[#c9a84c]/10 px-2 py-2.5 text-center text-[10.5px] leading-[1.4] text-white/75 last:border-b-0 sm:px-5 sm:py-4 sm:text-[13.5px]"
                    >
                      {r}
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <p className="text-center text-sm text-[#3a2e1e]">
              <strong>Please Note:</strong> 5% GST will be applicable on both the Hair Transplant packages.
            </p>

            <div className="mb-3 mt-7 pb-6 text-center">
              <a href="/khargharNaviMumbai.php" className={btnPrimary}>Book Now</a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}